import { Filter, FilterConfig } from 'src/utils/composeFilter.ts';
import { useCallback, useMemo } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { getFilterValuesFromUrl } from 'src/utils/getFilterValuesFromUrl.ts';
import { createCheckboxHandler } from 'src/modules/common/hooks/useFilterLogic/createCheckboxHandler.ts';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { useLocationState } from 'src/sensors/useLocationState.ts';

export function useFilterLogic(
    filterConfig: FilterConfig,
): [
        URLSearchParams,
        Filter,
        CheckboxHandler,
        (detail: { name: string; value: string }) => Filter,
        (config: FilterConfig, state?: unknown) => void,
        SetURLSearchParams,
    ] {
    const [searchParams, setSearchParams] = useSearchParams();
    const [locationState] = useLocationState();

    const filter = useMemo(
        () => getFilterValuesFromUrl(searchParams, filterConfig),
        [searchParams, filterConfig],
    );

    const checkboxHandlerInstance = useMemo(
        () => createCheckboxHandler(filter, searchParams, setSearchParams, locationState),
        [filter, locationState, searchParams, setSearchParams],
    );

    const checkboxHandler = useCallback(
        (value: { name: string; value: string | string[] }) => checkboxHandlerInstance(value),
        [checkboxHandlerInstance],
    );

    const radioHandler = useCallback((detail: { name: string; value: string }): Filter => {
        const newFilter = {
            ...filter,
            [detail.name]: [detail.value],
        };
        setSearchParams((search) => {
            Object
                .entries(newFilter)
                .forEach(([name, v]) => {
                    search.set(name, JSON.stringify(v));
                });
            return search;
        }, { replace: true, state: locationState });
        return newFilter;
    }, [filter, locationState, setSearchParams]);

    const resetHandler = useCallback((config: FilterConfig, state?: unknown) => {
        setSearchParams((search) => {
            config
                .forEach((item) => {
                    const [name] = item;
                    search.delete(name);
                });
            return search;
        }, {
            replace: true,
            ...(state !== undefined ? { state } : { state: locationState }),
        });
    }, [locationState, setSearchParams]);

    return [
        searchParams,
        filter,
        checkboxHandler,
        radioHandler,
        resetHandler,
        setSearchParams,
    ];
}
